// src/components/features/AddTptProductForm.js
import React, { useState, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { addDoc, collection, serverTimestamp, writeBatch, doc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { Loader2, PlusCircle, Users, Video, FileSpreadsheet } from 'lucide-react';
import SectionTitle from '../ui/SectionTitle';
import { analytics, db, functionsInstance } from '../../firebase/firebase';
import { extractProductNameFromUrl, extractIdFromUrl } from '../../utils/helpers';
import Papa from 'papaparse';

function stripHtmlToText(html) {
  return String(html || '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const AddTptProductForm = ({ currentUser, onProductSelect, triggerAuthModal, userTptProducts = [] }) => {
    const [newTptProductUrl, setNewTptProductUrl] = useState('');
    const [isFetchingTptDesc, setIsFetchingTptDesc] = useState(false);
    const [fetchTptDescError, setFetchTptDescError] = useState('');
    const [guestFreeUses, setGuestFreeUses] = useState({ tptUpload: 0 });

    // Refs and state for Excel Upload
    const fileInputRef = useRef(null);
    const [isBulkLoading, setIsBulkLoading] = useState(false);

    const handleAddTptProduct = async () => {
        if (!newTptProductUrl.trim()) return;

        const tptId = extractIdFromUrl(newTptProductUrl);
        if (currentUser && userTptProducts.length > 0) {
            const existing = userTptProducts.find(p => {
                const pId = p.tptProductId || extractIdFromUrl(p.tptUrl);
                return (tptId && pId === tptId) || p.tptUrl === newTptProductUrl;
            });

            if (existing) {
                alert("You already have this product in your dashboard! We've loaded it for you.");
                onProductSelect(existing);
                setNewTptProductUrl('');
                return;
            }
        }

        if (!currentUser) {
            if (guestFreeUses.tptUpload >= 1) {
                triggerAuthModal("Please sign up for a free trial to add more TPT products.", 'signup');
                return;
            }
            setGuestFreeUses((prev) => ({ ...prev, tptUpload: prev.tptUpload + 1 }));
        }

        if (!functionsInstance) {
            setFetchTptDescError("Core services are missing.");
            return;
        }
        if (!newTptProductUrl.includes('teacherspayteachers.com/Product/')) {
            setFetchTptDescError("Please enter a valid TPT Product URL.");
            return;
        }

        setIsFetchingTptDesc(true);
        setFetchTptDescError('');

        const fetchDescFunction = httpsCallable(functionsInstance, 'fetchTptPageDetails');

        try {
            const result = await fetchDescFunction({ url: newTptProductUrl });

            if (!currentUser) {
                const guestProduct = {
                    productTitle: result.data?.title || extractProductNameFromUrl(newTptProductUrl) || "Untitled",
                    originalDescription: result.data?.description || "",
                    price: result.data.price || 0,
                    scrapedGrades: result.data.scrapedGrades || [],
                    scrapedSubjects: result.data.scrapedSubjects || [],
                    scrapedTypes: result.data.scrapedTypes || [],
                    scrapedStandards: result.data.scrapedStandards || [],
                    rewrittenDescription: "",
                    tptUrl: newTptProductUrl,
                    tptProductId: tptId
                };
                onProductSelect(guestProduct);
                setNewTptProductUrl('');
                return;
            }

            const descriptionHtml = result.data?.description || "Description could not be fetched.";
            const descriptionText = stripHtmlToText(descriptionHtml);
            const hasDescription = descriptionText.length > 5 && !descriptionHtml.startsWith("Description could not be fetched.");

            const newProductData = {
                tptUrl: newTptProductUrl,
                tptProductId: tptId,
                productTitle: result.data?.title || extractProductNameFromUrl(newTptProductUrl) || "Untitled Product",
                originalDescription: descriptionHtml,
                descriptionHtml: descriptionHtml,
                descriptionText: descriptionText,
                price: result.data.price || 0,
                hasDescription: hasDescription,
                rewrittenDescription: "",
                addedAt: serverTimestamp(),
                lastScrapedAt: serverTimestamp(),
                scrapedGrades: result.data.scrapedGrades || [],
                scrapedSubjects: result.data.scrapedSubjects || [],
                scrapedTypes: result.data.scrapedTypes || [],
                scrapedStandards: result.data.scrapedStandards || []
            };

            const docRef = await addDoc(collection(db, "userProducts", currentUser.uid, "products"), newProductData);
            logEvent(analytics, 'add_tpt_product', { product_id: docRef.id, fetch_success: !!(newProductData.productTitle) });
            onProductSelect({ id: docRef.id, ...newProductData });
            setNewTptProductUrl('');

        } catch (fetchError) {
            console.error("Error adding product:", fetchError);
            setFetchTptDescError(`Failed to add product: ${fetchError.message || 'Unknown error.'}`);
        } finally {
            setIsFetchingTptDesc(false);
        }
    };

    const handleExcelUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // --- UPDATED CLEAR NOTICE ---
        const notice = "PLEASE NOTE:\n\n" +
            "1. Use the 'Import all products' tool in the sidebar if you have less than 1,000 products.\n\n" +
            "2. If you have MORE than 1,000 products and find any are missing after using the sidebar tool, please reach out to us via email at info@mrjoelstptgenerator.com for a full product export.\n\n" +
            "Do you want to proceed with this Excel upload?";

        if (!window.confirm(notice)) {
            e.target.value = null;
            return;
        }

        setIsBulkLoading(true);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                const rows = results.data;
                const urls = rows.map(row => row.URL || row.url || row.ProductUrl || Object.values(row)[0]);
                const validUrls = urls.filter(u => u && u.includes('teacherspayteachers.com/Product/'));

                if (validUrls.length === 0) {
                    alert("No valid TPT URLs found in the file.");
                    setIsBulkLoading(false);
                    return;
                }

                try {
                    const bulkImportFunc = httpsCallable(functionsInstance, 'bulkImportUrlsFromList');
                    await bulkImportFunc({ urls: validUrls });

                    alert(`Import started! We are scraping ${validUrls.length} products in the background. They will appear in your sidebar automatically as they finish.`);
                } catch (err) {
                    console.error("Bulk Scrape Error:", err);
                    alert("Import failed: " + err.message);
                } finally {
                    setIsBulkLoading(false);
                    e.target.value = null;
                }
            }
        });
    };

    return (
        <div className="relative bg-white p-6 sm:p-8 rounded-xl shadow-2xl space-y-6 mb-8">
            <a
                href="https://youtu.be/q2GyjPQVb28?si=vYYRmKfENuw_LL_B"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-6 right-6 flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
            >
                <Video size={14} />
                Watch Tutorial
            </a>
            <SectionTitle icon={Users}>Add TPT Product</SectionTitle>

            <div className="text-xs text-slate-500 -mt-3 mb-3 leading-relaxed space-y-2">
                {currentUser ? (
                    <>
                        <p><strong>Option 1:</strong> Use the input below to upload <strong>single products</strong> using the TPT product URL.</p>
                        <p><strong>Option 2:</strong> To import your <strong>entire store</strong>, please use the "Import all products" button in the sidebar.</p>
                        <p><strong>Option 3:</strong> If your store has <strong>over 1,000 products</strong>, please use the sidebar option first. If any are missing, reach out to us via email to use the <strong>Excel Upload</strong> option below.</p>
                    </>
                ) : (
                    <p>Guests: Add a TPT product URL to pre-fill the form below.</p>
                )}
            </div>

            <div className="flex flex-col gap-4">
                {/* Manual URL Input Row */}
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        type="url"
                        value={newTptProductUrl}
                        onChange={(e) => setNewTptProductUrl(e.target.value)}
                        placeholder="Paste TPT Product URL here..."
                        className="flex-grow p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
                        disabled={isFetchingTptDesc || isBulkLoading}
                    />
                    <button
                        type="button"
                        onClick={handleAddTptProduct}
                        disabled={isFetchingTptDesc || isBulkLoading || !newTptProductUrl.trim()}
                        className="bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 text-white font-semibold py-3 px-4 rounded-lg shadow-sm flex items-center justify-center transition-colors min-w-[140px]"
                    >
                        {isFetchingTptDesc ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <PlusCircle className="h-5 w-5 mr-2" />}
                        {currentUser ? "Add Product" : "Pre-fill"}
                    </button>
                </div>

                {/* Excel Upload Section */}
                {currentUser && (
                    <div className="pt-2 border-t border-slate-100">
                        <input
                            type="file"
                            accept=".csv"
                            className="hidden"
                            ref={fileInputRef}
                            onChange={handleExcelUpload}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current.click()}
                            disabled={isBulkLoading || isFetchingTptDesc}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 text-sm text-slate-600 border border-slate-300 hover:bg-slate-50 px-4 py-2.5 rounded-lg transition-all shadow-sm"
                        >
                            {isBulkLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <FileSpreadsheet size={18} className="text-emerald-600" />
                            )}
                            Excel Upload (1,000+ Products)
                        </button>
                    </div>
                )}
            </div>

            {fetchTptDescError && <p className="text-sm text-red-600 mt-2">{fetchTptDescError}</p>}
        </div>
    );
};

export default AddTptProductForm;